import { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCheckCircle,
  FiChevronRight,
  FiRotateCcw,
  FiXCircle
} = FiIcons;

function readBestScore(lessonId) {
  try {
    return JSON.parse(localStorage.getItem(`quiz_scores_${lessonId}`)) || null;
  } catch {
    return null;
  }
}

function QuizResults({ score, total, bestScore, onRetake }) {
  const percentage = Math.round((score / total) * 100);

  return (
    <div className="quiz-results">
      <span className="results-icon">
        <SafeIcon icon={FiCheckCircle} />
      </span>
      <span className="content-label">Quiz complete</span>
      <h2>{percentage}%</h2>
      <p>You answered {score} of {total} questions correctly.</p>
      <div className="results-summary">
        <span><strong>{score}</strong> Correct</span>
        <span><strong>{total - score}</strong> Incorrect</span>
        {bestScore && <span><strong>{bestScore.percentage}%</strong> Best score</span>}
      </div>
      <button className="lesson-complete-cta" onClick={onRetake}>
        <SafeIcon icon={FiRotateCcw} />
        Retake Quiz
      </button>
    </div>
  );
}

function QuizQuestion({
  question,
  questionIndex,
  selectedAnswer,
  onSelect,
  isActive
}) {
  const answered = selectedAnswer !== null;

  return (
    <article
      className="quiz-question-card"
      style={{ display: isActive ? 'block' : 'none' }}
      aria-hidden={!isActive}
    >
      <h3>{question.question}</h3>
      <div className="quiz-options">
        {question.options.map((option, index) => {
          const isCorrect = index === question.answer;
          const isSelected = index === selectedAnswer;
          let className = 'quiz-option';

          if (answered && isCorrect) className += ' is-correct';
          if (answered && isSelected && !isCorrect) className += ' is-incorrect';

          return (
            <button
              className={className}
              key={option}
              onClick={() => onSelect(index)}
              disabled={!isActive || answered}
            >
              <span className="option-letter">
                {String.fromCharCode(65 + index)}
              </span>
              <span>{option}</span>
              {answered && isCorrect && <SafeIcon icon={FiCheckCircle} />}
              {answered && isSelected && !isCorrect && (
                <SafeIcon icon={FiXCircle} />
              )}
            </button>
          );
        })}
      </div>

      {answered && (
        <div
          className={
            selectedAnswer === question.answer
              ? 'quiz-feedback is-correct'
              : 'quiz-feedback is-incorrect'
          }
        >
          <strong>
            {selectedAnswer === question.answer ? 'Correct!' : 'Not quite.'}
          </strong>
          <p>{question.explanation}</p>
        </div>
      )}
    </article>
  );
}

function QuizPanel({ lesson }) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(() => readBestScore(lesson.id));
  const [finished, setFinished] = useState(false);

  const question = lesson.quiz[questionIndex];
  const answered = selectedAnswer !== null;

  const progress = useMemo(
    () => ((questionIndex + (answered ? 1 : 0)) / lesson.quiz.length) * 100,
    [answered, lesson.quiz.length, questionIndex]
  );

  useEffect(() => {
    setQuestionIndex(0);
    setSelectedAnswer(null);
    setScore(0);
    setFinished(false);
    setBestScore(readBestScore(lesson.id));
  }, [lesson.id]);

  const selectAnswer = (answerIndex) => {
    if (answered) return;

    setSelectedAnswer(answerIndex);
    if (answerIndex === question.answer) {
      setScore((current) => current + 1);
    }
  };

  const nextQuestion = () => {
    if (questionIndex === lesson.quiz.length - 1) {
      const finalScore = score;
      const percentage = Math.round((finalScore / lesson.quiz.length) * 100);
      const existing = readBestScore(lesson.id);

      if (!existing || percentage > existing.percentage) {
        const nextBest = {
          score: finalScore,
          total: lesson.quiz.length,
          percentage,
          savedAt: new Date().toISOString()
        };

        localStorage.setItem(
          `quiz_scores_${lesson.id}`,
          JSON.stringify(nextBest)
        );
        setBestScore(nextBest);
      }

      setFinished(true);
      return;
    }

    setQuestionIndex((current) => current + 1);
    setSelectedAnswer(null);
  };

  const retake = () => {
    setQuestionIndex(0);
    setSelectedAnswer(null);
    setScore(0);
    setFinished(false);
  };

  if (finished) {
    return (
      <QuizResults
        score={score}
        total={lesson.quiz.length}
        bestScore={bestScore}
        onRetake={retake}
      />
    );
  }

  return (
    <div className="quiz-view">
      <div className="study-panel-heading">
        <div>
          <span className="content-label">Knowledge check</span>
          <h2>Practice quiz</h2>
        </div>
        <span className="quiz-counter">
          Question {questionIndex + 1} of {lesson.quiz.length}
        </span>
      </div>

      <div className="quiz-progress">
        <span style={{ width: `${progress}%` }} />
      </div>

      <div className="quiz-question-list">
        {lesson.quiz.map((item, index) => (
          <QuizQuestion
            key={item.id}
            question={item}
            questionIndex={index}
            selectedAnswer={index === questionIndex ? selectedAnswer : null}
            onSelect={selectAnswer}
            isActive={index === questionIndex}
          />
        ))}
      </div>

      <button
        className="lesson-complete-cta quiz-next-button"
        onClick={nextQuestion}
        disabled={!answered}
      >
        {questionIndex === lesson.quiz.length - 1
          ? 'See Results'
          : 'Next Question'}
        <SafeIcon icon={FiChevronRight} />
      </button>
    </div>
  );
}

export default QuizPanel;